import type { SubscriptionPlan } from '../../types/subscription';
import type { TermKind } from '../../types/billing';
import { Field } from './billingUi';
import { inputClass } from './billingHelpers';
import type { TermDraft } from './termDrafts';

/**
 * Discount / free-period fields. Terms never stack — the server applies the
 * single best one per invoice — so the hint says so where the choice is made.
 */
export default function TermFields({ draft, onChange, plans, idPrefix }: {
  draft: TermDraft;
  onChange: (d: TermDraft) => void;
  plans: SubscriptionPlan[];
  idPrefix: string;
}) {
  const set = (patch: Partial<TermDraft>) => onChange({ ...draft, ...patch });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Type" htmlFor={`${idPrefix}-kind`}>
        <select id={`${idPrefix}-kind`} value={draft.kind} onChange={e => set({ kind: e.target.value as TermKind })} className={inputClass}>
          <option value="discount_percent">Percentage off</option>
          <option value="discount_fixed">Dollars off per month</option>
          <option value="free_period">Free period</option>
        </select>
      </Field>
      <Field label="Applies to" htmlFor={`${idPrefix}-plan`}>
        <select id={`${idPrefix}-plan`} value={draft.plan_id} onChange={e => set({ plan_id: e.target.value })} className={inputClass}>
          <option value="">All modules</option>
          {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      {draft.kind !== 'free_period' && (
        <Field label={draft.kind === 'discount_percent' ? 'Percent off' : '$ off per month'} htmlFor={`${idPrefix}-value`}>
          <input id={`${idPrefix}-value`} type="number" min={0} step="0.01" value={draft.value} onChange={e => set({ value: e.target.value })} className={inputClass} />
        </Field>
      )}
      <Field label="Starts" htmlFor={`${idPrefix}-start`} hint="Blank = today">
        <input id={`${idPrefix}-start`} type="date" value={draft.starts_on} onChange={e => set({ starts_on: e.target.value })} className={inputClass} />
      </Field>
      <Field label={draft.kind === 'free_period' ? 'Free until' : 'Ends'} htmlFor={`${idPrefix}-end`} hint={draft.kind === 'free_period' ? undefined : 'Blank = no end'}>
        <input id={`${idPrefix}-end`} type="date" value={draft.ends_on} onChange={e => set({ ends_on: e.target.value })} className={inputClass} />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Reason" htmlFor={`${idPrefix}-reason`} hint="Only one offer applies per invoice — the one worth the most. Offers never add together.">
          <input id={`${idPrefix}-reason`} type="text" value={draft.reason} onChange={e => set({ reason: e.target.value })} placeholder="e.g. Hort Connections 2026 show special" className={inputClass} />
        </Field>
      </div>
    </div>
  );
}
